import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { IconPlus, IconEdit, IconTrash, IconFileInvoice, IconArrowUpRight, IconChevronDown, IconChevronUp, IconArrowBackUp } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    open: { label: 'Em aberto', color: 'default' },
    partial: { label: 'Parcial', color: 'warning' },
    overdue: { label: 'Vencida', color: 'error' },
    paid: { label: 'Paga', color: 'success' }
};

const emptyPaymentForm = {
    amount: '',
    discount_amount: '',
    interest_amount: '',
    fine_amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: '',
    bank_account: '',
    notes: ''
};

const ViewAllAccountsPayable = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [payables, setPayables] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [expanded, setExpanded] = useState({});
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);

    const [paymentDialog, setPaymentDialog] = useState(null); // { payableId, installment }
    const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarContas();
        (async () => {
            const res = await api.get(`v1/apps/${appId}/payment-methods/`);
            setPaymentMethods(res.data || []);
        })();
        (async () => {
            const defaultRes = await api.get('v1/apps/default/');
            const res = await api.get(`v1/apps/${appId}/bank-accounts/?company=${defaultRes.data.default_company_id}`);
            setBankAccounts(res.data.bank_accounts || []);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, statusFilter]);

    const listarContas = async () => {
        const query = statusFilter ? `?status=${statusFilter}` : '';
        const res = await api.get(`v1/apps/${appId}/payable/${query}`);
        setPayables(res.data.payables || []);
    };

    const refreshPayable = async (payableId) => {
        const res = await api.get(`v1/apps/${appId}/payable/${payableId}/`);
        setPayables((prev) => prev.map((p) => (p.id === payableId ? res.data : p)));
    };

    const toggleExpand = (payableId) => {
        setExpanded((prev) => ({ ...prev, [payableId]: !prev[payableId] }));
    };

    const handleDelete = async (payable) => {
        if (!window.confirm(`Excluir a conta "${payable.description}"? Isso remove todas as parcelas e pagamentos dela.`)) return;
        await api.delete(`v1/apps/${appId}/payable/${payable.id}/`);
        setPayables((prev) => prev.filter((p) => p.id !== payable.id));
    };

    const handleOpenPaymentDialog = (payableId, installment) => {
        setPaymentDialog({ payableId, installment });
        setPaymentForm({ ...emptyPaymentForm, amount: installment.remaining_balance });
    };

    const handleRegisterPayment = async () => {
        if (!paymentDialog) return;
        setSaving(true);
        try {
            await api.post(`v1/apps/${appId}/payable-installments/${paymentDialog.installment.id}/payments/`, {
                amount: paymentForm.amount || 0,
                discount_amount: paymentForm.discount_amount || 0,
                interest_amount: paymentForm.interest_amount || 0,
                fine_amount: paymentForm.fine_amount || 0,
                payment_date: paymentForm.payment_date,
                payment_method: paymentForm.payment_method || null,
                bank_account: paymentForm.bank_account || null,
                notes: paymentForm.notes || null
            });
            await refreshPayable(paymentDialog.payableId);
            setPaymentDialog(null);
        } catch (error) {
            console.error(error);
            window.alert(error.response?.data?.error || 'Erro ao registrar pagamento');
        } finally {
            setSaving(false);
        }
    };

    const handleReversePayment = async (payableId, payment) => {
        if (!window.confirm('Estornar esse pagamento?')) return;
        await api.post(`v1/apps/${appId}/payments/${payment.id}/reverse/`, {});
        await refreshPayable(payableId);
    };

    const handleReceipt = async (payment) => {
        // PDF é servido por uma rota autenticada (Bearer token) - não dá
        // pra só abrir a URL direto num link/nova aba, precisa passar pelo
        // axios (que injeta o header) e montar um blob URL local.
        const res = await api.get(`v1/apps/${appId}/payments/${payment.id}/receipt/`, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(blobUrl, '_blank');
    };

    const total = payables.reduce((acc, p) => acc + Number(p.remaining_balance), 0);

    return (
        <Box>
            <PageHeader
                icon={IconArrowUpRight}
                gradient="coral"
                title="Contas a Pagar"
                subtitle={`Total em aberto: ${formatCurrencyBR(total)}`}
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ width: 160 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="open">Em aberto</MenuItem>
                            <MenuItem value="partial">Parcial</MenuItem>
                            <MenuItem value="overdue">Vencida</MenuItem>
                            <MenuItem value="paid">Paga</MenuItem>
                        </TextField>

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/financial/payable/new')}
                        >
                            Nova Conta
                        </Button>
                    </Stack>
                }
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Descrição</TableCell>
                                <TableCell>Fornecedor</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Saldo</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payables.map((payable) => {
                                const statusInfo = STATUS_LABEL[payable.status] || { label: payable.status, color: 'default' };
                                const isExpanded = !!expanded[payable.id];
                                return (
                                    <React.Fragment key={payable.id}>
                                        <TableRow hover>
                                            <TableCell padding="checkbox">
                                                <IconButton size="small" onClick={() => toggleExpand(payable.id)}>
                                                    {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{payable.description}</TableCell>
                                            <TableCell>{payable.organization_name}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(payable.total_amount)}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(payable.remaining_balance)}</TableCell>
                                            <TableCell>
                                                <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => history.push(`/dashboard/financial/payable/${payable.id}/edit`)}
                                                >
                                                    <IconEdit size={16} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(payable)}>
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                                            Parcelas
                                                        </Typography>
                                                        {payable.installments.map((installment) => {
                                                            const instStatus = STATUS_LABEL[installment.status] || {
                                                                label: installment.status,
                                                                color: 'default'
                                                            };
                                                            return (
                                                                <Box
                                                                    key={installment.id}
                                                                    sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                                                                >
                                                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            Parcela {installment.number}/{installment.total_installments}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Valor: {formatCurrencyBR(installment.amount)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Vencimento: {formatDateBR(installment.due_date)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Saldo: {formatCurrencyBR(installment.remaining_balance)}
                                                                        </Typography>
                                                                        <Chip label={instStatus.label} color={instStatus.color} size="small" />
                                                                        {Number(installment.remaining_balance) > 0 && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => handleOpenPaymentDialog(payable.id, installment)}
                                                                            >
                                                                                Registrar pagamento
                                                                            </Button>
                                                                        )}
                                                                    </Stack>

                                                                    {installment.payments.length > 0 && (
                                                                        <Table size="small" sx={{ mt: 1 }}>
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell>Data</TableCell>
                                                                                    <TableCell align="right">Valor</TableCell>
                                                                                    <TableCell align="right">Juros</TableCell>
                                                                                    <TableCell align="right">Multa</TableCell>
                                                                                    <TableCell align="right">Desconto</TableCell>
                                                                                    <TableCell>Forma</TableCell>
                                                                                    <TableCell align="right" />
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {installment.payments.map((payment) => (
                                                                                    <TableRow key={payment.id}>
                                                                                        <TableCell
                                                                                            sx={{
                                                                                                textDecoration: payment.is_reversed
                                                                                                    ? 'line-through'
                                                                                                    : 'none'
                                                                                            }}
                                                                                        >
                                                                                            {formatDateBR(payment.payment_date)}
                                                                                            {payment.is_reversed && ' (estornado)'}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(payment.amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(payment.interest_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(payment.fine_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(payment.discount_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell>{payment.payment_method_name || '-'}</TableCell>
                                                                                        <TableCell align="right">
                                                                                            <Tooltip title="Ver recibo">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => handleReceipt(payment)}
                                                                                                >
                                                                                                    <IconFileInvoice size={14} />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            {!payment.is_reversed && (
                                                                                                <Tooltip title="Estornar">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() =>
                                                                                                            handleReversePayment(
                                                                                                                payable.id,
                                                                                                                payment
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        <IconArrowBackUp size={14} />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            )}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {payables.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhuma conta encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={!!paymentDialog} onClose={() => setPaymentDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Registrar pagamento
                    {paymentDialog && ` - Parcela ${paymentDialog.installment.number}/${paymentDialog.installment.total_installments}`}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Valor pago"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={paymentForm.amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data do pagamento"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={paymentForm.payment_date}
                                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Juros"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={paymentForm.interest_amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, interest_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Multa"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={paymentForm.fine_amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, fine_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Desconto"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={paymentForm.discount_amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, discount_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Forma de Pagamento"
                                fullWidth
                                value={paymentForm.payment_method}
                                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {paymentMethods.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Conta Bancária (opcional)"
                                fullWidth
                                value={paymentForm.bank_account}
                                onChange={(e) => setPaymentForm({ ...paymentForm, bank_account: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {bankAccounts.map((ba) => (
                                    <MenuItem key={ba.id} value={ba.id}>
                                        {ba.bank_name} - Ag {ba.agency_number} - CC {ba.account_number}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Notas"
                                fullWidth
                                multiline
                                minRows={2}
                                value={paymentForm.notes}
                                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialog(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleRegisterPayment} disabled={saving}>
                        {saving ? 'Salvando...' : 'Registrar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewAllAccountsPayable;
